// BM-H3636 - options the Wills questionnaire no longer offers (solicitor ruling, 5 Oct 2026).
//
// The Will cannot be written for these choices, so the client is not asked for them: nobody should spend
// forty minutes on an answer that has no wording. Two things follow.
//
//  1. Where a client might want one, the form says so and points to "Request a callback" (CallbackHint).
//  2. An answer already saved with a removed option is KEPT, never silently changed or dropped, and is
//     marked here as unsupported so the client sees it and the fee earner picks it up (UnsupportedNotice).
//
// What is saved is never rewritten to a supported value: a client who chose a substitute they named must not
// find the form has quietly turned it into "their children".

const CALLBACK = "Request a callback";

// The values that used to be offered and are not any more. Exported so a test can pin them against the screens.
export const REMOVED = {
  ipdiCommencement: ["after_s144_window"],
  remainderShape: ["discretionary_class", "onward_ipdi_impossible"],
  surplusRoute: ["ipdi_to_spouse", "outright_named"],
  homeStrategy: ["carveout_from_nrbdt"],
  legacySubstitution: ["to_named_substitute"],
  residueSubstitution: ["to_named_substitutes"],
  charityCeases: ["named_substitute_charity"],     // BM-H3932: "to a substitute charity I name"
};

// Paths are arrays of keys (not dotted strings): they are saved-answer paths, not question ids.
const get = (o, path) => path.reduce((x, k) => (x == null ? undefined : x[k]), o);

// One entry per saved answer that uses a removed option. section is the section key it belongs to.
export function findUnsupported(answers) {
  const a = answers || {};
  const out = [];
  const add = (section, id, said) => out.push({ section, id, said });

  if (get(a, ["s5_structure", "step_provisions_excluded"]) === true) {
    add("s5_structure", "step_opt_out", "You asked to leave out the standard set of administrative powers for your trustees.");
  }
  (get(a, ["s6_legacies", "specific"]) || []).forEach((g, i) => {
    if (REMOVED.legacySubstitution.includes(g?.substitution)) add("s6_legacies", `specific_${i}`, `For the item you give${g.description ? ` (${g.description})` : ""}, you chose a substitute you name if they die before you.`);
  });
  (get(a, ["s6_legacies", "pecuniary"]) || []).forEach((g, i) => {
    if (REMOVED.legacySubstitution.includes(g?.substitution)) add("s6_legacies", `pecuniary_${i}`, "For a sum of money you give, you chose a substitute you name if they die before you.");
  });
  // BM-H3932: a charity gift that goes to a substitute charity the client names if the charity no longer exists.
  (get(a, ["s6_legacies", "charity"]) || []).forEach((g, i) => {
    if (REMOVED.charityCeases.includes(g?.if_charity_ceases)) add("s6_legacies", `charity_${i}`, `For the gift to ${g.charity_name || "a charity"}, you chose a substitute charity you name if that charity no longer exists when you die.`);
  });
  (get(a, ["s2b_shares", "holdings"]) || []).forEach((h, i) => {
    if (h?.disposition === "business_succession_trust" && h?.trust_terms?.trust_type) {
      add("s2b_shares", `trust_type_${i}`, `For ${h.company_name || "a business interest"} you chose a ${h.trust_terms.trust_type === "fixed_interest" ? "fixed interest" : "discretionary"} trust. We no longer ask for the type online.`);
    }
  });
  if (REMOVED.residueSubstitution.includes(get(a, ["s7_residue", "substitution_rule"]))) {
    add("s7_residue", "residue_named_substitutes", "You chose a substitute you name for each person who is to receive the rest of your estate.");
  }
  if (REMOVED.ipdiCommencement.includes(get(a, ["s7_residue", "ipdi", "commencement"]))) {
    add("s7_residue", "ipdi_commencement", "You chose for the trust to start after a two year initial period.");
  }
  if (REMOVED.remainderShape.includes(get(a, ["s7_residue", "remainder_trusts", "shape"]))) {
    add("s7_residue", "remainder_shape", "You chose something other than named people, outright, to take the capital when the life interest ends.");
  }
  if (get(a, ["s7_residue", "termination_events", "on_cohabitation"]) === true) {
    add("s7_residue", "on_cohabitation", "You chose for the life interest to end if they live with a new partner.");
  }
  if (REMOVED.surplusRoute.includes(get(a, ["s7_residue", "nrbdt_scope", "surplus_residue_disposition", "route"]))) {
    add("s7_residue", "surplus_route", "You chose somewhere other than outright to your spouse or civil partner for the rest of the residue.");
  }
  if (REMOVED.homeStrategy.includes(get(a, ["s7_residue", "rnrb_home_planning", "strategy"]))) {
    add("s7_residue", "home_carveout", "You chose to carve your home out and pass it directly to your descendants.");
  }
  return out;
}

export function CallbackHint({ children, testid }) {
  return (
    <p data-testid={testid || "callback-hint"} style={{ fontSize: 13, color: "#334155", margin: "6px 0 14px" }}>
      {children} If you would like something different, use <strong>{CALLBACK}</strong> at the top of this page and your fee earner will go through it with you.
    </p>
  );
}

// Shown at the top of a section that holds a saved answer using a removed option.
export function UnsupportedNotice({ answers, section }) {
  const items = findUnsupported(answers).filter((x) => x.section === section);
  if (!items.length) return null;
  return (
    <div role="note" data-testid="unsupported-notice" style={{ border: "1px solid #b45309", background: "#fffbeb", borderRadius: 6, padding: "10px 12px", margin: "0 0 16px", fontSize: 14 }}>
      <strong>We no longer offer {items.length === 1 ? "this option" : "these options"} online.</strong>
      <ul style={{ margin: "6px 0 6px 18px" }}>
        {items.map((x) => <li key={x.id} data-testid={`unsupported-${x.id}`}>{x.said}</li>)}
      </ul>
      Your answer is saved and has not been changed. Your fee earner will go through it with you. Please use <strong>{CALLBACK}</strong> at the top of this page.
    </div>
  );
}
